import Link from "next/link"
import { FIRMS_VERIFIED_ON, LAW_FIRMS } from "@/lib/breach/law-firms"

const NEUTRAL = [
  { name: "ABA: Find Legal Help", url: "https://www.americanbar.org/groups/legal_services/flh-home/", note: "The American Bar Association's starting point for finding legal help. The ABA says it does not make referrals itself." },
  { name: "ABA: Lawyer Referral Directory", url: "https://www.americanbar.org/directories/lawyer-referral-directory/", note: "Find your state or local bar association's lawyer referral service, which can match you with a lawyer in your area." },
]

const QUESTIONS = [
  "Have you handled breaches for organizations like ours (size, industry, the kind of data involved)?",
  "Can you start now, and who will actually be working on it?",
  "Should the forensic investigation be engaged through your firm, and why?",
  "How do you charge, and what should we expect to spend in the first week?",
  "Do you represent any of our vendors, our insurer, or anyone else who could conflict with us?",
  "Which notification rules do you expect to apply to us, and what do you need from us to decide?",
]


/** The "find a lawyer" guidance, shared by the page and the in-tool panel. `panel` demotes headings one level. */
export function FindLawyerContent({ panel = false }: { panel?: boolean }) {
  const H2 = panel ? "h3" : "h2"
  const H3 = panel ? "h4" : "h3"
  return (
    <div>
        <H2 className="mt-10 text-2xl font-bold">1. Start with your cyber insurance</H2>
        <p className="mt-3 text-muted-foreground">
          If you have a cyber policy, read it or call its breach hotline first. Many policies set out which breach counsel and forensic firms the insurer approves and
          when you must give notice. Using someone outside that list, or waiting too long, can affect your coverage. Ask the insurer for a claim number and for their
          approved breach counsel. You can still talk to your own lawyer too.
        </p>

        <H2 className="mt-10 text-2xl font-bold">2. Use a bar referral service</H2>
        <p className="mt-3 text-muted-foreground">These are run by bar associations, not by us or by law firms that pay to be listed.</p>
        <ul className="mt-4 grid gap-3">
          {NEUTRAL.map((n) => (
            <li key={n.url} className="rounded-lg border border-border p-4">
              <a className="font-medium underline" href={n.url} target="_blank" rel="noopener">{n.name}</a>
              <p className="mt-1 text-sm text-muted-foreground">{n.note}</p>
            </li>
          ))}
        </ul>

        <H2 className="mt-10 text-2xl font-bold">3. Or look at firms that publicly advertise breach response</H2>
        <div className="mt-3 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">We have no affiliation or commercial relationship with any firm on this list.</strong> We receive no payment, referral fee, or other benefit
            from them, and they didn&apos;t ask to be listed. A listing is <strong className="text-foreground">not an endorsement or a recommendation</strong>, and the list is not a ranking.
            It is in alphabetical order.
          </p>
          <p className="mt-2">
            We selected these firms because each states on its own website that it handles data breach or incident response. We have not vetted their quality, fees, or availability and can&apos;t
            tell you whether one is right for you. Always check conflicts, experience, and fees yourself. Information was read from each firm&apos;s site on {FIRMS_VERIFIED_ON} and may be out of date.
            We list no phone numbers because we couldn&apos;t verify them: use the contact details on the firm&apos;s own site.
          </p>
        </div>
        <ul className="mt-6 grid gap-4">
          {LAW_FIRMS.map((f) => (
            <li key={f.name} className="rounded-lg border border-border p-4">
              <H3 className="text-lg font-semibold"><a className="underline" href={f.url} target="_blank" rel="noopener nofollow">{f.name}</a></H3>
              <p className="mt-1 text-sm text-muted-foreground">What the firm says: {f.says}</p>
              {f.smb && <p className="mt-1 text-sm text-muted-foreground">States on its site that it works with small and mid-size organizations.</p>}
              {f.tie && <p className="mt-1 text-sm"><strong>Note:</strong> <span className="text-muted-foreground">{f.tie} That is information from the firm&apos;s own site; ask about how it affects your matter.</span></p>}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          This list is not complete, and many excellent firms are not on it. Do you run a firm and want a correction? <Link href="/contact" className="underline">Tell us</Link>.
        </p>

        <H2 className="mt-10 text-2xl font-bold">Questions to ask any lawyer</H2>
        <ul className="mt-3 list-disc space-y-2 pl-6 text-muted-foreground">{QUESTIONS.map((q) => <li key={q}>{q}</li>)}</ul>

    </div>
  )
}
